import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Send, Mail, CheckCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SendQuoteEmailDialog } from "./SendQuoteEmailDialog";

interface SendQuoteButtonProps {
  leadId: string;
  currentStatus: string;
  clientEmail?: string | null;
  clientName?: string | null;
  leadInboundEmailId?: string | null;
}

export const SendQuoteButton = ({ 
  leadId, 
  currentStatus,
  clientEmail,
  clientName,
  leadInboundEmailId,
}: SendQuoteButtonProps) => {
  const [showChoiceDialog, setShowChoiceDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const queryClient = useQueryClient();

  const markAsSentMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // First, get current status from database to avoid race conditions
      const { data: leadData, error: fetchError } = await supabase
        .from("leads")
        .select("status")
        .eq("id", leadId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!leadData) throw new Error("Lead not found");

      const dbStatus = leadData.status;

      // Only update if lead is in an earlier stage (prevents downgrading from executing/confirmed)
      const { data: updateData, error } = await supabase
        .from("leads")
        .update({ status: "quote_sent" })
        .eq("id", leadId)
        .in("status", ["lead", "quote_generated"])
        .select("id");

      if (error) throw error;

      const statusActuallyChanged = updateData && updateData.length > 0;

      // Log the activity regardless (user did send the quote)
      await supabase.from("activity_log").insert({
        lead_id: leadId,
        user_id: user.id,
        action: "Quote Marked as Sent",
        details: statusActuallyChanged 
          ? "Quote marked as sent (sent externally)" 
          : `Quote marked as sent (status already progressed to ${dbStatus})`,
      });

      // Only log status change if it actually changed
      if (statusActuallyChanged) {
        await supabase.from("lead_changes").insert({
          lead_id: leadId,
          user_id: user.id,
          field_changed: "status",
          old_value: dbStatus,
          new_value: "quote_sent",
        });
      }

      return { statusActuallyChanged, dbStatus };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["activity-log", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead-changes", leadId] });
      if (result?.statusActuallyChanged) {
        toast.success("Quote marked as sent!");
      } else {
        toast.success(`Quote marked as sent. Status already progressed to "${result?.dbStatus}", so it was not changed.`);
      }
      setShowChoiceDialog(false);
    },
    onError: (error: any) => {
      toast.error(`Failed to update status: ${error.message}`);
    },
  });

  const handleEmailSent = async () => {
    // After email is sent, update the status
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: leadData } = await supabase
      .from("leads")
      .select("status")
      .eq("id", leadId)
      .maybeSingle();

    const dbStatus = leadData?.status;

    const { data: updateData } = await supabase
      .from("leads")
      .update({ status: "quote_sent" })
      .eq("id", leadId)
      .in("status", ["lead", "quote_generated"])
      .select("id");

    const statusActuallyChanged = updateData && updateData.length > 0;

    await supabase.from("activity_log").insert({
      lead_id: leadId,
      user_id: user.id,
      action: "Quote Sent via Email",
      details: `Quote sent to ${clientEmail}`,
    });

    if (statusActuallyChanged) {
      await supabase.from("lead_changes").insert({
        lead_id: leadId,
        user_id: user.id,
        field_changed: "status",
        old_value: dbStatus,
        new_value: "quote_sent",
      });
    }

    queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
    queryClient.invalidateQueries({ queryKey: ["leads"] });
    queryClient.invalidateQueries({ queryKey: ["activity-log", leadId] });
    queryClient.invalidateQueries({ queryKey: ["lead-changes", leadId] });
    
    toast.success("Quote sent via email!");
    setShowChoiceDialog(false);
  };

  // Only show button if status is quote_generated
  if (currentStatus !== "quote_generated") return null;

  const hasEmail = Boolean(clientEmail);

  return (
    <>
      <Button 
        onClick={() => setShowChoiceDialog(true)}
        className="gap-2"
        variant="default"
      >
        <Send className="h-4 w-4" />
        Send Quote
      </Button>

      {/* Choice Dialog */}
      <Dialog open={showChoiceDialog} onOpenChange={setShowChoiceDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Send Quote to Client</DialogTitle>
            <DialogDescription>
              How would you like to send this quote?
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-2">
            {clientName && (
              <p className="text-sm">
                <span className="text-muted-foreground">Client:</span>{" "}
                <span className="font-medium">{clientName}</span>
              </p>
            )}
            {clientEmail && (
              <p className="text-sm">
                <span className="text-muted-foreground">Email:</span>{" "}
                <span className="font-medium">{clientEmail}</span>
              </p>
            )}
          </div>

          <div className="grid gap-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      variant="default"
                      className="w-full gap-2 justify-start"
                      onClick={() => {
                        setShowChoiceDialog(false);
                        setShowEmailDialog(true);
                      }}
                      disabled={!hasEmail}
                    >
                      <Mail className="h-4 w-4" />
                      Send via Email
                      {!hasEmail && (
                        <span className="text-xs opacity-70 ml-auto">No email on file</span>
                      )}
                    </Button>
                  </div>
                </TooltipTrigger>
                {!hasEmail && (
                  <TooltipContent>
                    <p>No client email address on file. Please add an email to the lead first.</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            <Button
              variant="outline"
              className="w-full gap-2 justify-start"
              onClick={() => markAsSentMutation.mutate()}
              disabled={markAsSentMutation.isPending}
            >
              <CheckCircle className="h-4 w-4" />
              {markAsSentMutation.isPending ? "Updating..." : "Mark as Sent"}
              <span className="text-xs text-muted-foreground ml-auto">Sent externally</span>
            </Button>
          </div>

          <div className="pt-2">
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => setShowChoiceDialog(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Composition Dialog */}
      <SendQuoteEmailDialog
        open={showEmailDialog}
        onOpenChange={setShowEmailDialog}
        leadId={leadId}
        clientEmail={clientEmail || ""}
        clientName={clientName || ""}
        leadInboundEmailId={leadInboundEmailId}
        onSuccess={handleEmailSent}
      />
    </>
  );
};
